import ErrorLogger from 'entities/helpers/error-log-helper';

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// status/readyState 0 = the request never got an HTTP response (aborted, dropped, or reset
// connection); 502 = upstream connection failure (e.g. LB reusing a closed keep-alive socket).
// Neither means the data is missing or the server rejected the request.
export const isTransientFailure = (e) => e?.readyState === 0 || e?.status === 0 || e?.status === 502;

/**
 * Run a request factory, retrying transient transport failures with exponential backoff.
 *
 * Browsers only auto-retry idempotent GETs on dead connections; our GraphQL reads ride POST,
 * so without this a single connection reset renders pages empty ("Constituent not found",
 * "no steps") for data that exists (SIG-826). Real server errors are never retried.
 *
 * @param {Function} makeRequest - Factory returning a thenable (jqXHR). May return undefined
 *   when the requester is paused (session renewal) — treated as transient and re-issued.
 * @param {Object} [options]
 * @param {number} [options.retries=2] - Max retry attempts after the initial request.
 * @param {number} [options.baseDelayMs=300] - Backoff base; delay = base * 2^attempt.
 * @param {boolean} [options.enabled=true] - Set false for non-idempotent requests (mutations).
 * @param {string} [options.label='request'] - Identifier for telemetry.
 * @returns {Promise<*>} Resolves with the response; rejects with the last error.
 */
export const withTransientRetry = async (
  makeRequest,
  { retries = 2, baseDelayMs = 300, enabled = true, label = 'request' } = {}
) => {
  for (let attempt = 0; ; attempt++) {
    try {
      const request = makeRequest();
      if (request === undefined) {
        throw Object.assign(new Error(`${label} not sent: requester paused`), { status: 0, readyState: 0 });
      }
      return await request;
    } catch (e) {
      if (enabled && isTransientFailure(e) && attempt < retries) {
        await wait(baseDelayMs * 2 ** attempt);
        continue;
      }
      if (enabled && isTransientFailure(e)) {
        ErrorLogger.error(`Transient retry exhausted: ${label}`, {
          extra: {
            attempts: attempt + 1,
            status: e?.status,
            readyState: e?.readyState,
            statusText: e?.statusText,
            message: e?.message,
          },
        });
      }
      throw e;
    }
  }
};
